import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Switch,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Separator
} from '@tonic/shared/components/ui';
import { Save, RotateCw } from 'lucide-react';

export function RentalSettingsPage() {
  const handleSave = (e) => {
    e.preventDefault();
    // TODO: Implement settings save functionality
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Rental Settings</h1>
          <p className="text-sm text-neutral-500">Configure your rental program settings</p>
        </div>
        <Button onClick={handleSave}>Save Changes</Button>
      </div>

      <form className="space-y-6">
        {/* General Settings */}
        <Card>
          <CardHeader>
            <CardTitle>General Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Minimum Rental Period (days)</Label>
                <Input type="number" defaultValue={1} />
              </div>
              <div className="space-y-2">
                <Label>Maximum Rental Period (days)</Label>
                <Input type="number" defaultValue={30} />
              </div>
              <div className="space-y-2">
                <Label>Grace Period for Returns (hours)</Label>
                <Input type="number" defaultValue={24} />
              </div>
              <div className="space-y-2">
                <Label>Default Rental Duration</Label>
                <Select defaultValue="7">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Allow Early Returns</Label>
                <p className="text-sm text-neutral-500">Let customers return items before the rental period ends</p>
              </div>
              <Switch defaultChecked />
            </div>
          </CardContent>
        </Card>

        {/* Fees & Deposits */}
        <Card>
          <CardHeader>
            <CardTitle>Fees & Deposits</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Security Deposit (%)</Label>
                <Input type="number" defaultValue={50} />
              </div>
              <div className="space-y-2">
                <Label>Late Return Fee (per day)</Label>
                <Input type="number" defaultValue={10} />
              </div>
              <div className="space-y-2">
                <Label>Damage Assessment Fee</Label>
                <Input type="number" defaultValue={25} />
              </div>
              <div className="space-y-2">
                <Label>Cleaning Fee</Label>
                <Input type="number" defaultValue={15} />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Automatic Late Fees</Label>
                <p className="text-sm text-neutral-500">Automatically charge late fees for overdue items</p>
              </div>
              <Switch defaultChecked />
            </div>
          </CardContent>
        </Card>

        {/* Maintenance */}
        <Card>
          <CardHeader>
            <CardTitle>Maintenance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Maintenance Schedule</Label>
                <Select defaultValue="after_return">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="after_return">After Each Return</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="custom">Custom Schedule</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quality Check Frequency</Label>
                <Select defaultValue="each_rental">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="each_rental">Each Rental</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Maintenance Checklist</Label>
              <Textarea 
                placeholder="Enter maintenance checklist items..."
                defaultValue="1. Inspect for damage
2. Clean and sanitize
3. Check all components
4. Test functionality
5. Document condition"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle>Notifications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Rental Confirmation</Label>
                  <p className="text-sm text-neutral-500">Send confirmation when rental is booked</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Return Reminder</Label>
                  <p className="text-sm text-neutral-500">Send reminder before rental period ends</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Late Return Alert</Label>
                  <p className="text-sm text-neutral-500">Send notification when item is overdue</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Maintenance Updates</Label>
                  <p className="text-sm text-neutral-500">Send updates about item maintenance status</p>
                </div>
                <Switch defaultChecked />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reminder Lead Time (hours)</Label>
              <Input type="number" defaultValue={48} />
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
} 